import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, slugify } from '../../api/db';
import { useAuth } from '../../context/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiUsers, FiTag, FiX, FiSave, FiSettings, FiImage } = FiIcons;

const Dashboard = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '' });
  const navigate = useNavigate();
  const { isSuperuser } = useAuth();

  const loadData = async () => {
    const [arts, cats] = await Promise.all([
      db.getArticlesWithCategory(false),
      db.getCategories()
    ]);
    setArticles(arts);
    setCategories(cats);
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredArticles = useMemo(() => {
    if (!selectedCategoryId) return articles;
    return articles.filter(a => a.category?.id === selectedCategoryId);
  }, [articles, selectedCategoryId]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      await db.deleteArticle(id);
      loadData();
    }
  };

  const openCategoryModal = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', slug: '' });
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryForm({ name: '', slug: '' });
  };

  const handleCategoryFormChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'name' && (!prev.slug || prev.slug === slugify(prev.name))) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, slug: category.slug });
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      alert('Category name is required.');
      return;
    }
    try {
      await db.saveCategory({
        id: editingCategory?.id,
        name: categoryForm.name,
        slug: categoryForm.slug || slugify(categoryForm.name)
      });
      await loadData();
      closeCategoryModal();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await db.deleteCategory(id);
        await loadData();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage your blog articles</p>
        </div>
        <div className="flex items-center gap-3">
          {isSuperuser && (
            <button
              onClick={() => navigate('/admin/users')}
              className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiUsers} /> Manage Users
            </button>
          )}
          {isSuperuser && (
            <button
              onClick={() => navigate('/admin/settings')}
              className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiSettings} /> Site Settings
            </button>
          )}
          {isSuperuser && (
            <button
              onClick={openCategoryModal}
              className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiTag} /> Categories
            </button>
          )}
          <button
            onClick={() => navigate('/admin/media')}
            className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            <SafeIcon icon={FiImage} /> Media
          </button>
          <button
            onClick={() => navigate('/admin/editor/new')}
            className="flex items-center gap-2 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <SafeIcon icon={FiPlus} /> New Article
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="category-filter" className="text-sm font-medium text-gray-700">Filter by Category</label>
          <select
            id="category-filter"
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            className="block w-48 pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </div>
        <p className="text-sm text-gray-500">
          {filteredArticles.length} article{filteredArticles.length === 1 ? '' : 's'}
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Article</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredArticles.map((article) => (
              <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <a
                      href={article.status === 'published' ? `/#/blog/${article.slug}` : undefined}
                      target={article.status === 'published' ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      title={article.status === 'published' ? 'View published article' : 'Draft — not yet published'}
                      className={`h-10 w-10 flex-shrink-0 rounded bg-gray-100 overflow-hidden block ${article.status === 'published' ? 'cursor-pointer ring-0 hover:ring-2 transition-all' : 'cursor-default'}`}
                    >
                      <img
                        className="h-10 w-10 object-cover"
                        src={article.cover_image?.trim() || '/fallback.png'}
                        alt=""
                        onError={e => { e.currentTarget.src = '/fallback.png'; }}
                      />
                    </a>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{article.title || 'Untitled'}</div>
                      <div className="text-sm text-gray-500">/{article.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    article.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {article.status === 'published' ? <SafeIcon icon={FiEye} className="w-3 h-3 mr-1 mt-1" /> : <SafeIcon icon={FiEyeOff} className="w-3 h-3 mr-1 mt-1" />}
                    {article.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'var(--brand-primary-light)', color: 'var(--brand-primary)' }}>
                    {article.category?.name || 'Education'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {article.author_name || <span className="text-gray-300 italic">—</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {article.view_count > 0
                    ? <span className="inline-flex items-center gap-1 font-medium" style={{ color: 'var(--brand-primary)' }}>{article.view_count}</span>
                    : <span className="text-gray-300">0</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(article.updated_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => navigate(`/admin/editor/${article.id}`)}
                      className="p-1"
                      style={{ color: 'var(--brand-primary)' }}
                      title="Edit"
                    >
                      <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete"
                    >
                      <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {filteredArticles.length === 0 && (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  No articles found. Click "New Article" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Category Management Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Manage Categories</h2>
              <button onClick={closeCategoryModal} className="text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={handleSaveCategory} className="mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
                    <input
                      type="text"
                      name="name"
                      value={categoryForm.name}
                      onChange={handleCategoryFormChange}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g. Digital Skills"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                    <input
                      type="text"
                      name="slug"
                      value={categoryForm.slug}
                      onChange={handleCategoryFormChange}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                      placeholder="digital-skills"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                  >
                    <SafeIcon icon={FiSave} /> {editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Articles</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {categories.map(category => (
                      <tr key={category.id}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{category.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono">/{category.slug}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{category.article_count || 0}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditCategory(category)}
                              className="p-1"
                              style={{ color: 'var(--brand-primary)' }}
                              title="Edit"
                            >
                              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Delete"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {categories.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                          No categories yet. Create one above.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;